/**
 * Route-driven <head> metadata for the SPA. There is no SSR — only the public
 * routes (/, /login, /register, /contact and the account-recovery pages) are
 * crawlable — so per-route titles and meta descriptions are applied at runtime
 * from one place instead of pulling in a head-manager dependency. The static defaults live in index.html; robots.txt
 * and sitemap.xml (in public/) cover the crawler side.
 *
 * To retitle a page (or add a new one), edit the maps below — Layout reuses
 * PAGE_TITLES for its breadcrumb, so the two never drift apart.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const SITE_NAME = 'ALFIE';
export const DEFAULT_TITLE = 'ALFIE — AI Legal Research & Drafting';
export const DEFAULT_DESCRIPTION =
  'ALFIE is an AI legal research and drafting platform: grounded chat, document generation, '
  + 'summarization, citation checking, deadlines, and more.';

/** Human titles per route; used for document.title and the topbar breadcrumb. */
export const PAGE_TITLES: Record<string, string> = {
  '/': 'Chat',
  '/login': 'Log in',
  '/register': 'Create account',
  '/forgot-password': 'Forgot password',
  '/reset-password': 'Reset password',
  '/verify-email': 'Verify email',
  '/contact': 'Contact',
  '/status': 'System Status',
  '/dashboard': 'Dashboard',
  '/documents': 'Document Generator',
  '/drafter': 'Drafting Assistant',
  '/deconstructor': 'Brief Deconstructor',
  '/summarizer': 'Summarizer',
  '/analyzer': 'Document Analyzer',
  '/headnotes': 'Headnote Generator',
  '/precedent': 'Precedent Finder',
  '/citator': 'Citator',
  '/brief-audit': 'Brief Auditor',
  '/cite-check': 'Cite-Check Editor',
  '/redline': 'Redline / Compare',
  '/deadlines': 'Deadline Calculator',
  '/chronology': 'Chronology Builder',
  '/redaction': 'Redaction',
  '/gazette': 'Gazette',
  '/colloquy': 'Colloquy',
  '/valorem': 'Valorem',
  '/facts': 'Facts',
  '/matters': 'Matters',
  '/watches': 'Watches & Alerts',
  '/firm-brain': 'Firm Brain',
  '/orgs': 'Organization',
  '/research': 'Research Agent',
  '/engines': 'AI Engines',
  '/settings': 'Account',
  '/admin/users': 'Users',
  '/admin/audit': 'Audit Log',
  '/admin/ai': 'AI Providers',
  '/admin/library': 'Research Library',
  '/admin/layers': 'Enhancement Layers',
  '/admin/content': 'Forms & Templates',
  '/admin/ingestion': 'Ingestion',
  '/admin/connectors': 'Source Connectors',
  '/admin/valorem': 'Valorem',
  '/admin/deadline-packs': 'Court-Rules Packs',
};

/** Per-route meta descriptions. Only public routes matter for SEO — the rest are behind auth. */
const DESCRIPTIONS: Record<string, string> = {
  '/':
    'Ask legal questions and get grounded, citable answers from ALFIE, the AI legal research '
    + 'assistant. Free to try — no account required.',
  '/login': 'Log in to ALFIE, the AI legal research and drafting platform.',
  '/register':
    'Create a free ALFIE account to save conversations, generate legal documents, and ground '
    + 'answers in your own files.',
  '/forgot-password': 'Request a password reset link for your ALFIE account.',
  '/reset-password': 'Choose a new password for your ALFIE account.',
  '/verify-email': 'Confirm your email address to finish setting up your ALFIE account.',
  '/contact': 'Contact the ALFIE team with questions, support requests, or feedback.',
  '/status': 'Live connection, service health, background work and client diagnostics for your ALFIE session.',
};

/** Resolve the title for a pathname, handling the few parameterised routes. */
export function pageTitle(pathname: string): string | undefined {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (pathname.startsWith('/matters/')) return 'Matter';
  if (pathname.startsWith('/valorem/')) return 'Valorem';
  if (pathname.startsWith('/chat/')) return 'Chat';
  if (pathname.startsWith('/documents/new/')) return 'New Document';
  if (pathname.startsWith('/documents/')) return 'Document';
  if (pathname.startsWith('/drafter/')) return 'Draft';
  if (pathname.startsWith('/deconstructor/')) return 'Brief Deconstructor';
  if (pathname.startsWith('/research/')) return 'Research Run';
  if (pathname.startsWith('/cite-check/')) return 'Cite-Check Editor';
  if (pathname.startsWith('/sources/')) return 'Authority';
  if (pathname.startsWith('/watches/alerts/')) return 'Change alert';
  if (pathname.startsWith('/analyzer/')) return 'Document Analyzer';
  if (pathname.startsWith('/redline/')) return 'Redline / Compare';
  if (pathname.startsWith('/colloquy/')) return 'Colloquy';
  if (pathname.startsWith('/gazette/publications/')) return 'Gazette Publication';
  if (pathname.startsWith('/gazette/')) return 'Gazette Issue';
  return undefined;
}

export function applyPageMeta(pathname: string): void {
  const title = pageTitle(pathname);
  // The chat home keeps the full descriptive title — it is the indexable landing page.
  document.title = title && pathname !== '/' ? `${title} · ${SITE_NAME}` : DEFAULT_TITLE;
  document
    .querySelector('meta[name="description"]')
    ?.setAttribute('content', DESCRIPTIONS[pathname] ?? DEFAULT_DESCRIPTION);
}

/** Keeps document.title + meta description in sync with the current route. */
export function usePageMeta(): void {
  const { pathname } = useLocation();
  useEffect(() => {
    applyPageMeta(pathname);
  }, [pathname]);
}
